import { AllocationSupplier } from '@gateway.Simulation'
import { FC, PropsWithChildren, useMemo } from 'react'
import ReactDataSheet from 'react-datasheet'
import { useAppSelector } from '../../../stores'

interface CellData extends ReactDataSheet.Cell<CellData, number> {
  value: number | null
}

const SheetRenderer: FC<PropsWithChildren<{
  className: string}>
> = (props) => {
  const displayStartYear = Number(useAppSelector((state) => state.common.displayStartYear))
  const months = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]
  return (
    <div className="scrollable-container" style={{ minHeight: 90, maxHeight: 203 }}>
      <table
        className={props.className}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th colSpan={2} rowSpan={2} style={{ position: 'sticky' }}>
              <div style={{ width: 360 }}>業者名</div>
            </th>
            <th colSpan={12}>{displayStartYear}</th>
            <th colSpan={12}>{displayStartYear + 1}</th>
            <th colSpan={12}>{displayStartYear + 2}</th>
            <th colSpan={12}>{displayStartYear + 3}</th>
            <th colSpan={12}>{displayStartYear + 4}</th>
            <th colSpan={12}>{displayStartYear + 5}</th>
          </tr>
          <tr>
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{props.children}</tbody>
      </table>
    </div>
  )
}

const RowRenderer: FC<PropsWithChildren<{
  row: number
  rowLength: number
  list: AllocationSupplier
}>> = ({ row, rowLength, list, children }) => {
  if (row === rowLength - 1) {
    return (
      <tr>
        <th
          colSpan={2}
          style={{
            position: 'sticky',
            height: '29px',
            textAlign: 'left',
            width: 360
          }}
        >
          <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>合計</span>
        </th>
        {children}
      </tr>
    )
  }

  return (
    <tr>
      <th
        colSpan={2}
        style={{
          position: 'sticky',
          height: '29px',
          textAlign: 'left',
          width: 365
        }}
      >
        <span style={{ marginLeft: '8px' }}>
          {list.supplierNm}
        </span>
      </th>
      {children}
    </tr>
  )
}

const ValueViewer: FC<{
  valueViewerProps: ReactDataSheet.ValueViewerProps<CellData, number>
  rowLength: number
}> = (props) => {
  return <div style={{ height: '100%' }}>{props.valueViewerProps.value}</div>
}

interface DispatchAllotmentSheetProps {
  allocationSupplierList: AllocationSupplier[]
}

export const DispatchAllotmentSheet: FC<DispatchAllotmentSheetProps> = ({
  allocationSupplierList
}) => {
  const data = useMemo(() => {
    if (allocationSupplierList.length === 0) {
      const data: CellData[] = []
      for (let i = 0; i < 72; i++) {
        data.push({ value: 0, readOnly: true })
      }
      return [data]
    }
    const data: CellData[][] = []
    allocationSupplierList.forEach((item) => {
      if (item.workQuantity !== null) {
        const newArr = item.workQuantity.map(
          (workQuantityItem: number | null) => {
            return {
              value: workQuantityItem,
              readOnly: true
            }
          }
        )
        data.push(newArr)
      } else {
        data.push([])
      }
    })

    data.push([])
    // 合計の計算
    for (let i = 0; i < data[0].length; i++) {
      let sum = 0
      for (let j = 0; j < data.length - 1; j++) {
        sum += data[j][i].value ?? 0
      }
      data[data.length - 1][i] = {
        value: sum,
        readOnly: true
      }
    }
    return data
  }, [allocationSupplierList])

  const valueViewer = (
    valueViewerProps: ReactDataSheet.ValueViewerProps<CellData, number>
  ) => {
    return (
      <ValueViewer
        valueViewerProps={valueViewerProps}
        rowLength={data.length}
      />
    )
  }
  const sheetRenderer = (
    sheetRendererProps: ReactDataSheet.SheetRendererProps<CellData, number>
  ) => {
    return (
      <SheetRenderer className={sheetRendererProps.className}>
        {sheetRendererProps.children}
      </SheetRenderer>
    )
  }

  const rowRenderer = (
    rowRendererProps: ReactDataSheet.RowRendererProps<CellData, number>
  ) => {
    const list = allocationSupplierList[rowRendererProps.row]
    return (
      <RowRenderer
        row={rowRendererProps.row}
        rowLength={data.length}
        list={list}
      >
        {rowRendererProps.children}
      </RowRenderer>
    )
  }

  return (
    <div style={{ padding: '0 16px' }}>
      <ReactDataSheet
        data={data}
        valueRenderer={(cell) => cell.value}
        valueViewer={valueViewer}
        sheetRenderer={sheetRenderer}
        rowRenderer={rowRenderer}
      />
    </div>
  )
}
